import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-lienholder-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { links } from '../../../../shared/styles/links.style.js';
import { style } from './lender-edit.style.js';
import { LenderService } from '../../../../services/lender.service.js';
import { pageHeaders } from '../../../../shared/styles/page-headers.style.js';
import { stratum } from '../../../../../constants/stratum.js';
import '../../../shared/lender-name/lender-name.component.js';
import '../../../shared/lender-account-number/lender-account-number.component.js';
import '../../../shared/lender-payoff-amount/lender-payoff-amount.component.js';
import '@cof/fs-button-wc/fs-button.js';
import { preventEvDefault } from '../../../../shared/decorators/prevent-ev-default.decorator.js';
import { vibrate } from '../../../../shared/decorators/vibrate.decorator.js';
import { validateForm } from '../../../../shared/decorators/validate-form.decorator.js';

class RefiLenderEdit extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiLenderedit');
    this.apiErrorMessage = '';
    this.loading = false;
    this.lenderName = '';
    this.lenderInfo = {};
  }

  static get properties() {
    return {
      apiErrorMessage: { type: String },
      loading: { type: Boolean },
      lenderInfo: { type: Object },
      lenderInfoPrefilled: { type: Boolean },
    };
  }

  static get styles() {
    return [pageHeaders, links, style];
  }

  connectedCallback() {
    super.connectedCallback();
    this.loading = false;
    this.lenderInfo = LenderService.getLienholder();
  }

  firstUpdated() {
    stratum.publishTag(TagKey.VIEW_LENDER_CHECKOUT);
  }

  render() {
    this.debug.log('rendered lienholder-edit');
    return html`
      <form autocomplete="off" class="lender-edit-form" @submit="${this.updateLender}" id="lender-edit-form">
        <div class="grid-rows">
          <div>
            <div class="grid-columns">
              <refi-lender-name id="refi-lender-name" @unsaved-fields="${this.dispatchUnsavedFieldsEvent}"></refi-lender-name>
              <refi-lender-account-number id="refi-lender-account-number" @unsaved-fields="${this.dispatchUnsavedFieldsEvent}"></refi-lender-account-number>
            </div>
          </div>
          <div>
            <div class="grid-columns">
              <refi-lender-payoff-amount id="refi-lender-payoff-amount"></refi-lender-payoff-amount>
              <div></div>
            </div>
          </div>
          <div>
            <div class="grid-columns">
              <div class="button-container">
                <div class="mobile-order">
                  <fs-button
                    class="fs-button update-lender-button"
                    variant="primary"
                    type="submit"
                    @click="${() => {
                      stratum.publishTag(TagKey.LENDER_CHECKOUT_UPDATE_LENDER_CLICKED);
                    }}"
                    ?loading="${this.loading}"
                    >Update lender</fs-button
                  >
                  <fs-button class="fs-button cancel-lender-button" variant="secondary" type="button" @click="${this.cancel}">Cancel</fs-button>
                </div>
                <div class="desktop-order">
                  <fs-button class="fs-button cancel-lender-button" variant="secondary" type="button" @click="${this.cancel}">Cancel</fs-button>
                  <fs-button
                    class="fs-button update-lender-button"
                    variant="primary"
                    type="submit"
                    @click="${() => {
                      stratum.publishTag(TagKey.LENDER_CHECKOUT_UPDATE_LENDER_CLICKED);
                    }}"
                    ?loading="${this.loading}"
                    >Update lender</fs-button
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    `;
  }

  @preventEvDefault()
  @vibrate(50)
  @validateForm(false)
  async updateLender() {
    this.debug.log('submitted');
    const lenderName = this.shadowRoot.getElementById('refi-lender-name').shadowRoot.getElementById('lenderName').value;
    const lenderAccountNumber = this.shadowRoot.getElementById('refi-lender-account-number').shadowRoot.getElementById('lenderAccountNumber').value;

    if (!(lenderName === this.lenderInfo.lienHolderName && lenderAccountNumber === this.lenderInfo.lienHolderAccountNumber)) {
      if (lenderName !== this.lenderInfo.lienHolderName) {
        stratum.publishTag(TagKey.LIENHOLDER_LENDER_INFO_CHANGED, {
          replacements: {
            value: 'lenderName',
          },
        });
      }
      if (lenderAccountNumber !== this.lenderInfo.lienHolderAccountNumber) {
        stratum.publishTag(TagKey.LIENHOLDER_LENDER_INFO_CHANGED, {
          replacements: {
            value: 'lenderAccountNumber',
          },
        });
      }
      this.loading = true;
      this.lenderInfo.lienHolderName = lenderName;
      this.lenderInfo.lienHolderAccountNumber = lenderAccountNumber;
      this.debug.log('Calling persist Lienholder.');
      let res = await LenderService.persistLienHolder(this.lenderInfo, false);
      if (res.status === 204) {
        this.apiErrorMessage = '';
        stratum.publishTag(TagKey.LIENHOLDER_LENDER_UPDATES_SAVE_SUCCESS);
        // send event to host-app that we're done updating.
        LenderService.setLienholder(this.lenderInfo);
        this.dispatchCloseEvent('update');
        this.loading = false;
      } else {
        // try 1 more time
        setTimeout(async () => {
          res = await LenderService.persistLienHolder(this.lenderInfo, false);
          if (res.status === 204) {
            this.apiErrorMessage = '';
            stratum.publishTag(TagKey.LIENHOLDER_LENDER_UPDATES_SAVE_SUCCESS);
            LenderService.setLienholder(this.lenderInfo);
            this.dispatchCloseEvent('update');
            this.loading = false;
          } else {
            // breaking error
            this.debug.log('Errors from persist lienholder.');
            this.dispatchFailedToUpdate();
          }
          this.loading = false;
        }, 1000);
      }
    } else {
      // no update needed
      this.debug.log('Values have not changed, no update needed.');
      this.dispatchCloseEvent('update');
    }
  }

  async cancel() {
    this.debug.log('cancelled');
    this.dispatchCloseEvent('cancel');
    stratum.publishTag(TagKey.LENDER_CHECKOUT_CANCEL_LENDER_CLICKED);
  }

  dispatchUnsavedFieldsEvent() {
    this.debug.log('edit unsaved fields event');
    this.dispatchEvent(
      new CustomEvent('dispatch-unsaved-missing-fields', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: 'unsaved',
        },
      }),
    );
  }

  dispatchFailedToUpdate() {
    this.dispatchEvent(new CustomEvent('dispatch-failed-load-update-event'));
    stratum.publishTag(TagKey.LIENHOLDER_LENDER_UPDATES_SAVE_FAILED);
  }

  dispatchCloseEvent(action) {
    this.dispatchEvent(
      new CustomEvent('dispatch-close-event', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: action,
          id: 'lender-card',
        },
      }),
    );
  }
}

customElements.define('refi-lender-edit', RefiLenderEdit);
